import { RichtextContentsLinksFetchedData } from "@robe/core";
import { ContentLink } from "@robe/client";
import { Box, Button, Flex, Link, Typography } from "@xcorejs/ui";
import { RenderElementProps } from "slate-react";
import { ElementProps } from "./types";
import { getIdSlug, sanitizeHtml } from "./utils";
import { h1Base, h2Base, h3Base, h4 } from "./styles";

//! Do not use heading elements inside of span or paragraph, it causes hydration errors
//! Do not use paragraph inside of paragraph, it causes hydration errors
//! Do not use anchor tags inside of anchor tags, it causes hydration errors
export const renderElement =
  ({ _paragraph, _link, _img, _liClassName, _ulClassName }: ElementProps, contentsSlugs?: RichtextContentsLinksFetchedData) =>
    ({ children, element: { children: value, ...element }, ...attributes }: RenderElementProps & { element: any }) => {
    // Prevent rendering empty attributes in DOM
      if (typeof attributes.attributes === "object" && Object.keys(attributes.attributes).length === 0) {
        attributes = {} as any;
      }

      switch (element.type) {
        case "ul":
          return <ul className={`rt-ul rt-list ${_ulClassName ?? ""}`}>{children}</ul>;
        case "heading-one": {
          const id = getIdSlug(value);
          return (
            <span id={id} className="rt-heading-one" style={h1Base}>
              {children}
            </span>
          );
        }
        case "heading-two": {
          const id = getIdSlug(value);
          return (
            <span id={id} className="rt-heading-two" style={h2Base}>
              {children}
            </span>
          );
        }
        case "heading-three": {
          const id = getIdSlug(value);

          return (
            <span id={id} className="rt-heading-three" style={h3Base}>
              {children}
            </span>
          );
        }
        case "heading-four": {
          const id = getIdSlug(value);
          return (
            <span id={id} style={h4}>
              {children}
            </span>
          );
        }
        case "li":
          return (
            <li className={`rt-li${_liClassName ? " " + _liClassName : ""}${element?.liFontSize ? ` rt-li-${element.liFontSize}` : ""}`}>
              <div className="rt-li-content-wrapper">{children}</div>
            </li>
          );
        case "ol":
          return (
            <ol {...attributes} className={`rt-list ${_ulClassName ?? ""}`}>
              {children}
            </ol>
          );
        case "contentLink":
        case "link":
          return element.linkType === "external" ? (
            <Link
              {...attributes}
              href={element.url}
              {...element.styles}
              {...(element?.liFontSize && {
                className: `rt-li-${element.liFontSize}`,
              })}
              {...(element.styles?.target && { rel: "noopener noreferrer" })}
              {..._link}
            >
              {children}
            </Link>
          ) : contentsSlugs && contentsSlugs[element.content]?.contentType && contentsSlugs[element.content]?.slug ? (
            <ContentLink
              {...attributes}
              {..._link}
              inline
              content={{
                id: element.content,
                type: contentsSlugs[element.content].contentType,
                slug: contentsSlugs[element.content].slug,
              }}
              {...element.styles}
              {...(element?.liFontSize && {
                className: `rt-li-${element.liFontSize}`,
              })}
            >
              {children}
            </ContentLink>
          ) : (
            <></>
          );
        case "paragraph":
          return (
            <Typography
              variant="p"
              {..._paragraph}
              {...attributes}
              {...element.styles}
              {...(element?.liFontSize && {
                className: `rt-li-${element.liFontSize}`,
              })}
            >
              {children}
            </Typography>
          );
        case "button":
          if (element.linkType === "external") {
            return (
              <Button
                as="a"
                {...attributes}
                color="white"
                href={element.url}
                {...element.styles}
                {...(element.styles?.target && { rel: "noopener noreferrer" })}
                {..._link}
              >
                <span color="white">{children}</span>
              </Button>
            );
          }

          if (contentsSlugs && contentsSlugs[element.content]?.contentType && contentsSlugs[element.content]?.slug) {
            return (
              <ContentLink
                {...attributes}
                {..._link}
                {...element.styles}
                content={{
                  id: element.content,
                  type: contentsSlugs[element.content].contentType,
                  slug: contentsSlugs[element.content].slug,
                }}
              >
                <Button color="white">{children}</Button>
              </ContentLink>
            );
          }

          return <></>;
        case "html":
          const sanitizedHtml = sanitizeHtml(element.src);
          if (!sanitizedHtml) {
            return <div>Invalid HTML</div>;
          }

          return <Typography dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />;
        case "img":
          const { caption, ...rest } = element;

          return (
            <Flex {...attributes} alignItems="center" flexDirection="column">
              <Box as="img" {...rest} maxHeight="20rem" {...(_img as any)} />
              {caption && (
                <Typography mt="1rem" variant="p">
                  {caption}
                </Typography>
              )}
            </Flex>
          );
        default:
          return (
            <Typography
              variant="span"
              {..._paragraph}
              {...attributes}
              {...element.styles}
              {...(element?.liFontSize && {
                className: `rt-li-${element.liFontSize}`,
              })}
            >
              {children}
            </Typography>
          );
      }
    };
